/**
 * PDEPathway.jsx
 *
 * Pyridoxine-Dependent Epilepsy — the cofactor heist.
 *
 * Dependencies:
 *   npm install gsap smiles-drawer
 *
 * Usage:
 *   import PDEPathway from './PDEPathway';
 *   <PDEPathway />
 */

import { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import SmilesDrawer from 'smiles-drawer/src/SmilesDrawer';
import ContactFooter from '../components/ContactFooter';

gsap.registerPlugin(ScrollTrigger);

// ─── FONTS ────────────────────────────────────────────────────────────────────
const FONT_URL =
  'https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=IBM+Plex+Mono:wght@400;500&family=Syne:wght@400;600&display=swap';

// ─── COLOR TOKENS ─────────────────────────────────────────────────────────────
// PDE runs a bright LIGHT theme — white background, vivid accents — to set it
// apart from the dark palette used everywhere else on the site.
const C = {
  bg:       '#ffffff',
  bgAlt:    '#f3f1fb',
  ink:      '#1b1530',
  dim:      '#6c6685',
  violet:   '#7c3aed',
  violetLo: 'rgba(124,58,237,0.55)',
  amber:    '#d97706',
  amberLo:  'rgba(217,119,6,0.55)',
  storm:    '#e11d6b',
  stormLo:  'rgba(225,29,107,0.6)',
  calm:     '#0d9488',
};

// ─── STEP DATA ─────────────────────────────────────────────────────────────────
const STEPS = [
  {
    num: '01 / 05',
    chapter: 'Baseline',
    title: 'Lysine, cleared.',
    body: 'In healthy cells the antiquitin enzyme (ALDH7A1) oxidizes α-AASA in the lysine catabolic stream. PLP — the active form of vitamin B6 — circulates freely as a cofactor for ~140 enzymes, including GAD which synthesizes the inhibitory neurotransmitter GABA.',
    badge: 'Cofactor available',
    tone: 'ok',
    show: ['lys', 'antq', 'aaa', 'plp-pool', 'gad', 'gaba', 'flow-1', 'flow-2', 'flow-3'],
    hide: ['antq-x', 'p6c', 'trap', 'storm', 'rescue'],
    eeg: 'calm',
  },
  {
    num: '02 / 05',
    chapter: 'Lesion',
    title: 'Antiquitin fails.',
    body: 'Biallelic loss-of-function mutations in ALDH7A1 abolish antiquitin activity. The lysine degradation conveyor jams one step in. α-AASA cannot proceed to α-aminoadipate.',
    badge: 'ALDH7A1 deficiency',
    tone: 'bad',
    show: ['lys', 'antq-x', 'plp-pool', 'gad', 'gaba', 'flow-1'],
    hide: ['antq', 'aaa', 'p6c', 'trap', 'flow-2', 'flow-3', 'storm', 'rescue'],
    eeg: 'calm',
  },
  {
    num: '03 / 05',
    chapter: 'Heist',
    title: 'PLP is stolen.',
    body: 'α-AASA equilibrates with Δ¹-piperideine-6-carboxylate (P6C). P6C performs a Knoevenagel-type condensation with PLP, locking the cofactor into an inert adduct. The free PLP pool collapses.',
    badge: 'Cofactor sequestration',
    tone: 'bad',
    show: ['lys', 'antq-x', 'p6c', 'trap', 'plp-pool', 'flow-1'],
    hide: ['antq', 'aaa', 'gad', 'gaba', 'flow-2', 'flow-3', 'storm', 'rescue'],
    eeg: 'flutter',
  },
  {
    num: '04 / 05',
    chapter: 'Storm',
    title: 'GABA collapses.',
    body: 'Glutamate decarboxylase, starved of PLP, can no longer synthesize GABA. Inhibitory tone evaporates; cortical networks fire unchecked. Untreated, neonates present within hours of birth with intractable, anticonvulsant-refractory seizures.',
    badge: 'Neonatal seizure',
    tone: 'bad',
    show: ['lys', 'antq-x', 'p6c', 'trap', 'storm'],
    hide: ['antq', 'aaa', 'plp-pool', 'gad', 'gaba', 'flow-1', 'flow-2', 'flow-3', 'rescue'],
    eeg: 'seize',
  },
  {
    num: '05 / 05',
    chapter: 'Rescue',
    title: 'Flood with B6.',
    body: '· Pharmacologic pyridoxine (15–30 mg/kg/day) overwhelms the P6C trap and rebuilds the PLP pool\n· Lysine-restricted diet reduces P6C production at source\n· Arginine supplementation competes for lysine transport\n· Triple therapy now standard; cognitive outcomes still variable',
    badge: 'B6-responsive',
    tone: 'ok',
    show: ['lys', 'antq-x', 'p6c', 'plp-pool', 'gad', 'gaba', 'rescue', 'flow-1', 'flow-2', 'flow-3'],
    hide: ['antq', 'aaa', 'trap', 'storm'],
    eeg: 'calm',
  },
];

// ─── MOLECULE DATA ─────────────────────────────────────────────────────────────
const MOLS = {
  lysine: {
    name: 'L-Lysine',
    smiles: 'N[C@@H](CCCCN)C(=O)O',
    role: 'Substrate',
    desc: 'Essential amino acid. Restricting dietary lysine reduces P6C generation — one arm of triple therapy.',
  },
  aasa: {
    name: 'α-AASA',
    smiles: 'O=CCCC[C@H](N)C(=O)O',
    role: 'Accumulates',
    desc: 'α-Aminoadipic semialdehyde. Antiquitin substrate; the biomarker that confirms diagnosis in urine and plasma.',
  },
  p6c: {
    name: 'P6C',
    smiles: 'O=C(O)C1CCCC=N1',
    role: 'Trap',
    desc: 'Δ¹-Piperideine-6-carboxylate. The cyclic form of AASA. Condenses with PLP\'s aldehyde to form an inactive adduct.',
  },
  plp: {
    name: 'PLP',
    smiles: 'Cc1ncc(COP(=O)(O)O)c(C=O)c1O',
    role: 'Cofactor',
    desc: 'Pyridoxal 5\'-phosphate — the active B6 species. Reactive aldehyde is exactly what makes it both useful and steal-able.',
  },
  pyridoxine: {
    name: 'Pyridoxine',
    smiles: 'Cc1ncc(CO)c(CO)c1O',
    role: 'Therapy',
    desc: 'The B6 vitamer given pharmacologically. Phosphorylated to PLP in vivo; high doses outpace the P6C sink.',
  },
  gaba: {
    name: 'GABA',
    smiles: 'NCCCC(=O)O',
    role: 'Output',
    desc: 'Principal inhibitory neurotransmitter. Synthesized by GAD, which requires PLP. No PLP, no GABA, no inhibition.',
  },
};

// ─── SOURCES ───────────────────────────────────────────────────────────────────
const SOURCES = [
  {
    cite: 'Mills PB, Struys E, Jakobs C, et al. Mutations in antiquitin in individuals with pyridoxine-dependent seizures. Nat Med. 2006;12(3):307–309.',
    href: 'https://doi.org/10.1038/nm1366',
  },
  {
    cite: 'Coughlin CR 2nd, Tseng LA, Bok LA, et al. Consensus guidelines for the diagnosis and management of pyridoxine-dependent epilepsy due to α-aminoadipic semialdehyde dehydrogenase deficiency. J Inherit Metab Dis. 2021;44(1):178–192.',
    href: 'https://doi.org/10.1002/jimd.12332',
  },
  {
    cite: 'Gospe SM Jr. Pyridoxine-Dependent Epilepsy – ALDH7A1. GeneReviews. University of Washington, Seattle; updated 2022.',
    href: 'https://www.ncbi.nlm.nih.gov/books/NBK1486/',
  },
  {
    cite: 'Online Mendelian Inheritance in Man (OMIM). #266100 — Pyridoxine-dependent epilepsy.',
    href: 'https://omim.org/entry/266100',
  },
  {
    cite: 'Struys EA, Jakobs C. Metabolism of lysine in α-aminoadipic semialdehyde dehydrogenase-deficient fibroblasts: evidence for an alternative pathway of pipecolic acid formation. FEBS Lett. 2010;584(1):181–186.',
    href: 'https://doi.org/10.1016/j.febslet.2009.11.055',
  },
  {
    cite: 'Hallen A, Jamie JF, Cooper AJL. Lysine metabolism in mammalian brain: an update on the importance of recent discoveries. Amino Acids. 2013;45(6):1249–1272.',
    href: 'https://doi.org/10.1007/s00726-013-1590-1',
  },
];

// ─── STYLES ───────────────────────────────────────────────────────────────────
const S = {
  root: {
    background: C.bg,
    color: C.ink,
    fontFamily: "'Syne', sans-serif",
    overflowX: 'hidden',
  },
  // Hero
  hero: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    padding: '2rem',
    borderBottom: '1px solid rgba(27,21,48,0.10)',
    position: 'relative',
    overflow: 'hidden',
  },
  heroEEG: {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    opacity: 0.18,
    pointerEvents: 'none',
  },
  heroTag: {
    fontFamily: "'IBM Plex Mono', monospace",
    fontSize: '0.65rem',
    letterSpacing: '0.3em',
    textTransform: 'uppercase',
    color: C.amber,
    marginBottom: '1.5rem',
    opacity: 0,
    zIndex: 1,
  },
  heroH1: {
    fontFamily: "'Instrument Serif', serif",
    fontSize: 'clamp(3.5rem, 9vw, 8rem)',
    lineHeight: 0.95,
    letterSpacing: '-0.02em',
    marginBottom: '1.5rem',
    opacity: 0,
    zIndex: 1,
  },
  heroP: {
    maxWidth: '460px',
    color: C.dim,
    fontSize: '0.95rem',
    lineHeight: 1.7,
    fontFamily: "'IBM Plex Mono', monospace",
    opacity: 0,
    zIndex: 1,
  },
  heroMeta: {
    position: 'absolute',
    bottom: '2rem',
    left: 0,
    right: 0,
    display: 'flex',
    justifyContent: 'space-between',
    padding: '0 3rem',
    fontFamily: "'IBM Plex Mono', monospace",
    fontSize: '0.6rem',
    letterSpacing: '0.2em',
    color: C.dim,
    textTransform: 'uppercase',
    opacity: 0,
  },
  // Pathway: chapter rail on left, diagram center, copy right
  pathwaySection: { position: 'relative' },
  pathwaySticky: {
    position: 'relative',
    height: '100vh',
    display: 'grid',
    gridTemplateColumns: '140px 1fr 380px',
    alignItems: 'center',
    padding: '0 5vw',
    gap: '3rem',
    overflow: 'hidden',
  },
  chapterRail: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.5rem',
    fontFamily: "'IBM Plex Mono', monospace",
    fontSize: '0.6rem',
    letterSpacing: '0.18em',
    textTransform: 'uppercase',
  },
  chapterItem: (active) => ({
    color: active ? C.violet : C.dim,
    paddingLeft: '1rem',
    borderLeft: active ? `2px solid ${C.violet}` : '2px solid rgba(27,21,48,0.10)',
    transition: 'all 0.4s',
    opacity: active ? 1 : 0.5,
  }),
  diagramWrap: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.8rem',
    alignItems: 'stretch',
  },
  eegStrip: {
    width: '100%',
    height: 70,
    border: '1px solid rgba(27,21,48,0.10)',
    background: 'rgba(27,21,48,0.025)',
    position: 'relative',
  },
  eegLabel: {
    position: 'absolute',
    top: 8, left: 10,
    fontFamily: "'IBM Plex Mono', monospace",
    fontSize: '0.55rem',
    letterSpacing: '0.2em',
    color: C.dim,
    textTransform: 'uppercase',
  },
  eegReading: (mode) => ({
    position: 'absolute',
    top: 8, right: 10,
    fontFamily: "'IBM Plex Mono', monospace",
    fontSize: '0.55rem',
    letterSpacing: '0.2em',
    textTransform: 'uppercase',
    color: mode === 'seize' ? C.storm : mode === 'flutter' ? C.amber : C.calm,
  }),
  stepPanel: { position: 'relative' },
  stepNum: {
    fontFamily: "'IBM Plex Mono', monospace",
    fontSize: '0.6rem',
    letterSpacing: '0.2em',
    color: C.dim,
    textTransform: 'uppercase',
    marginBottom: '0.6rem',
  },
  stepTitle: {
    fontFamily: "'Instrument Serif', serif",
    fontSize: 'clamp(1.8rem, 2.8vw, 2.6rem)',
    lineHeight: 1.05,
    marginBottom: '1.2rem',
    letterSpacing: '-0.01em',
  },
  stepBody: {
    fontFamily: "'IBM Plex Mono', monospace",
    fontSize: '0.78rem',
    lineHeight: 1.85,
    color: C.dim,
    whiteSpace: 'pre-line',
  },
  // Catabolic cascade section
  cascadeSection: {
    padding: '8rem 6vw 7rem',
    borderTop: '1px solid rgba(27,21,48,0.10)',
    background: C.bg,
    position: 'relative',
  },
  cascadeHead: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    flexWrap: 'wrap',
    gap: '2rem',
    maxWidth: '1200px',
    marginBottom: '3rem',
  },
  cascadeLabel: {
    fontFamily: "'IBM Plex Mono', monospace",
    fontSize: '0.6rem',
    letterSpacing: '0.25em',
    color: C.violet,
    textTransform: 'uppercase',
    marginBottom: '0.8rem',
  },
  cascadeHeading: {
    fontFamily: "'Instrument Serif', serif",
    fontSize: 'clamp(2rem, 4vw, 3.4rem)',
    lineHeight: 1.05,
    maxWidth: '620px',
  },
  cascadeBlurb: {
    fontFamily: "'IBM Plex Mono', monospace",
    fontSize: '0.78rem',
    lineHeight: 1.8,
    color: C.dim,
    maxWidth: '380px',
  },
  cascadeSVGWrap: {
    width: '100%',
    overflowX: 'auto',
    paddingBottom: '1rem',
  },
  cascadeLegend: {
    display: 'flex',
    gap: '2rem',
    flexWrap: 'wrap',
    marginTop: '2rem',
    fontFamily: "'IBM Plex Mono', monospace",
    fontSize: '0.65rem',
    letterSpacing: '0.12em',
    color: C.dim,
    textTransform: 'uppercase',
  },
  legendSwatch: (color) => ({
    display: 'inline-block',
    width: 10, height: 10,
    background: color,
    marginRight: 8,
    verticalAlign: 'middle',
  }),
  // Rescue interactive
  rescueSection: {
    padding: '8rem 6vw',
    borderTop: '1px solid rgba(27,21,48,0.10)',
    background: C.bgAlt,
    position: 'relative',
  },
  rescueGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '5rem',
    alignItems: 'center',
    maxWidth: '1100px',
  },
  rescueLabel: {
    fontFamily: "'IBM Plex Mono', monospace",
    fontSize: '0.6rem',
    letterSpacing: '0.25em',
    color: C.amber,
    textTransform: 'uppercase',
    marginBottom: '0.8rem',
  },
  rescueHeading: {
    fontFamily: "'Instrument Serif', serif",
    fontSize: 'clamp(2rem, 4vw, 3.4rem)',
    lineHeight: 1.05,
    marginBottom: '1.5rem',
  },
  rescueCopy: {
    fontFamily: "'IBM Plex Mono', monospace",
    fontSize: '0.78rem',
    lineHeight: 1.8,
    color: C.dim,
    marginBottom: '2rem',
  },
  doseToggle: {
    display: 'inline-flex',
    border: '1px solid rgba(27,21,48,0.14)',
    fontFamily: "'IBM Plex Mono', monospace",
    fontSize: '0.7rem',
  },
  doseBtn: (active) => ({
    padding: '0.7rem 1.4rem',
    border: 'none',
    background: active ? `rgba(217,119,6,0.14)` : 'transparent',
    color: active ? C.amber : C.dim,
    cursor: 'pointer',
    letterSpacing: '0.1em',
    transition: 'all 0.3s',
    textTransform: 'uppercase',
    borderRight: '1px solid rgba(27,21,48,0.10)',
  }),
  // Molecule viewer
  viewerSection: {
    padding: '8rem 6vw',
    borderTop: '1px solid rgba(27,21,48,0.10)',
    background: C.bg,
  },
  viewerLabel: {
    fontFamily: "'IBM Plex Mono', monospace",
    fontSize: '0.6rem',
    letterSpacing: '0.25em',
    color: C.violet,
    textTransform: 'uppercase',
    marginBottom: '0.8rem',
  },
  viewerHeading: {
    fontFamily: "'Instrument Serif', serif",
    fontSize: 'clamp(2rem, 4vw, 3.4rem)',
    marginBottom: '3rem',
  },
  molGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
    gap: '1rem',
    maxWidth: '1100px',
  },
  molCard: (active) => ({
    border: active ? `1px solid ${C.violet}` : '1px solid rgba(27,21,48,0.10)',
    background: active ? 'rgba(124,58,237,0.06)' : 'rgba(27,21,48,0.025)',
    padding: '1.2rem',
    cursor: 'pointer',
    transition: 'all 0.3s',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.6rem',
    minHeight: '280px',
  }),
  molRole: (tone) => ({
    fontFamily: "'IBM Plex Mono', monospace",
    fontSize: '0.55rem',
    letterSpacing: '0.2em',
    textTransform: 'uppercase',
    color: tone === 'Therapy' ? C.amber : tone === 'Trap' || tone === 'Accumulates' ? C.storm : C.violet,
  }),
  molName: {
    fontFamily: "'Instrument Serif', serif",
    fontSize: '1.5rem',
  },
  molCanvasBox: {
    background: 'rgba(27,21,48,0.035)',
    border: '1px solid rgba(27,21,48,0.07)',
    minHeight: 140,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0.5rem',
  },
  molDesc: {
    fontFamily: "'IBM Plex Mono', monospace",
    fontSize: '0.7rem',
    color: C.dim,
    lineHeight: 1.7,
  },
  // Sources
  sourcesSection: {
    padding: '8rem 6vw 4rem',
    borderTop: '1px solid rgba(27,21,48,0.10)',
    background: C.bgAlt,
  },
  sourcesLabel: {
    fontFamily: "'IBM Plex Mono', monospace",
    fontSize: '0.6rem',
    letterSpacing: '0.25em',
    color: C.violet,
    textTransform: 'uppercase',
    marginBottom: '0.8rem',
  },
  sourcesHeading: {
    fontFamily: "'Instrument Serif', serif",
    fontSize: 'clamp(2rem, 4vw, 3.4rem)',
    marginBottom: '2.5rem',
  },
  sourceRow: {
    display: 'flex',
    gap: '1.2rem',
    padding: '1.1rem 0',
    borderTop: '1px solid rgba(27,21,48,0.10)',
    maxWidth: '860px',
  },
  sourceIdx: {
    fontFamily: "'IBM Plex Mono', monospace",
    fontSize: '0.7rem',
    color: C.violet,
    flexShrink: 0,
    width: '1.6rem',
  },
  sourceCite: {
    fontFamily: "'IBM Plex Mono', monospace",
    fontSize: '0.75rem',
    lineHeight: 1.75,
    color: '#6c6685',
    textDecoration: 'none',
  },
  footer: {
    padding: '3rem 6vw 5rem',
    fontFamily: "'IBM Plex Mono', monospace",
    fontSize: '0.6rem',
    letterSpacing: '0.2em',
    color: C.dim,
    textTransform: 'uppercase',
  },
};

function badgeStyle(tone) {
  const ok = tone === 'ok';
  return {
    display: 'inline-block',
    marginTop: '1.5rem',
    fontFamily: "'IBM Plex Mono', monospace",
    fontSize: '0.6rem',
    letterSpacing: '0.15em',
    textTransform: 'uppercase',
    padding: '0.35rem 0.85rem',
    background: ok ? 'rgba(13,148,136,0.12)' : 'rgba(225,29,107,0.07)',
    color: ok ? C.calm : C.storm,
    border: ok ? `1px solid rgba(13,148,136,0.4)` : `1px solid rgba(225,29,107,0.35)`,
  };
}

// ─── EEG WAVEFORM ─────────────────────────────────────────────────────────────
// Animated SVG that renders calm / fluttering / seizing rhythms.
function EEG({ mode = 'calm', width = 720, height = 70, color }) {
  const pathRef = useRef(null);
  const tRef = useRef(0);

  useEffect(() => {
    let raf;
    const step = () => {
      tRef.current += mode === 'seize' ? 0.22 : mode === 'flutter' ? 0.08 : 0.04;
      const t = tRef.current;
      const pts = [];
      const N = 240;
      for (let i = 0; i <= N; i++) {
        const x = (i / N) * width;
        const phase = i * 0.18 + t;
        let y;
        if (mode === 'seize') {
          y =
            Math.sin(phase * 2.2) * 18 +
            Math.sin(phase * 5.1 + t * 0.7) * 14 +
            (Math.random() - 0.5) * 16;
        } else if (mode === 'flutter') {
          y =
            Math.sin(phase * 1.3) * 8 +
            Math.sin(phase * 3.2 + 1) * 5 +
            (Math.random() - 0.5) * 4;
        } else {
          y = Math.sin(phase * 0.7) * 5 + Math.sin(phase * 1.6 + 0.4) * 3;
        }
        pts.push(`${x.toFixed(1)},${(height / 2 + y).toFixed(1)}`);
      }
      if (pathRef.current) pathRef.current.setAttribute('points', pts.join(' '));
      raf = requestAnimationFrame(step);
    };
    step();
    return () => cancelAnimationFrame(raf);
  }, [mode, width, height]);

  const stroke =
    color || (mode === 'seize' ? C.storm : mode === 'flutter' ? C.amber : C.calm);

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      style={{ width: '100%', height: '100%', display: 'block' }}
    >
      <polyline
        ref={pathRef}
        fill="none"
        stroke={stroke}
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ transition: 'stroke 0.5s' }}
      />
    </svg>
  );
}

// ─── PATHWAY SVG ──────────────────────────────────────────────────────────────
// Vertical "drain" metaphor: lysine flowing down through antiquitin gate,
// PLP pool on the right being siphoned into the P6C trap.
function PathwaySVG({ vis }) {
  const o = (id) => vis[id] ?? 0;
  return (
    <svg
      viewBox="0 0 520 360"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ width: '100%', maxWidth: '620px', height: 'auto', overflow: 'visible' }}
    >
      <defs>
        <marker id="ma-v" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
          <path d="M0,0 L0,6 L7,3z" fill={C.violetLo} />
        </marker>
        <marker id="ma-a" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
          <path d="M0,0 L0,6 L7,3z" fill={C.amberLo} />
        </marker>
        <marker id="ma-s" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
          <path d="M0,0 L0,6 L7,3z" fill={C.stormLo} />
        </marker>
        <filter id="glow-v">
          <feGaussianBlur stdDeviation="4" result="b" />
          <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
        <filter id="glow-s">
          <feGaussianBlur stdDeviation="6" result="b" />
          <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
        <linearGradient id="pool-grad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={C.amber} stopOpacity="0.35" />
          <stop offset="100%" stopColor={C.amber} stopOpacity="0.05" />
        </linearGradient>
        <pattern id="dots" width="6" height="6" patternUnits="userSpaceOnUse">
          <circle cx="1" cy="1" r="0.6" fill="rgba(27,21,48,0.16)" />
        </pattern>
      </defs>

      {/* central spine column */}
      <line x1="160" y1="40" x2="160" y2="320" stroke="rgba(27,21,48,0.10)" strokeWidth="1" strokeDasharray="2 4" />

      {/* Lysine — top */}
      <g id="lys" opacity={o('lys')} style={{ transition: 'opacity 0.5s' }}>
        <circle cx="160" cy="55" r="32" fill={C.bg} stroke={C.violetLo} strokeWidth="1.5" />
        <text x="160" y="52" textAnchor="middle" fontFamily="Syne" fontSize="11" fill={C.ink}>Lysine</text>
        <text x="160" y="66" textAnchor="middle" fontFamily="IBM Plex Mono" fontSize="7.5" fill={C.dim}>C₆H₁₄N₂O₂</text>
      </g>

      {/* flow 1: lys → antiquitin gate */}
      <line id="flow-1" opacity={o('flow-1')} x1="160" y1="90" x2="160" y2="118" stroke={C.violetLo} strokeWidth="1.5" markerEnd="url(#ma-v)" style={{ transition: 'opacity 0.5s' }} />

      {/* Antiquitin — gate (working) */}
      <g id="antq" opacity={o('antq')} style={{ transition: 'opacity 0.5s' }}>
        <rect x="100" y="120" width="120" height="38" rx="3" fill="rgba(124,58,237,0.08)" stroke={C.violetLo} strokeWidth="1.2" />
        <text x="160" y="137" textAnchor="middle" fontFamily="IBM Plex Mono" fontSize="9" fill={C.violet}>ANTIQUITIN</text>
        <text x="160" y="151" textAnchor="middle" fontFamily="IBM Plex Mono" fontSize="7.5" fill={C.dim}>ALDH7A1 · active</text>
      </g>

      {/* Antiquitin — gate (broken) */}
      <g id="antq-x" opacity={o('antq-x')} style={{ transition: 'opacity 0.5s' }}>
        <rect x="100" y="120" width="120" height="38" rx="3" fill="rgba(225,29,107,0.07)" stroke={C.stormLo} strokeWidth="1.4" strokeDasharray="3 3" />
        <text x="160" y="137" textAnchor="middle" fontFamily="IBM Plex Mono" fontSize="9" fill={C.storm}>ANTIQUITIN ✕</text>
        <text x="160" y="151" textAnchor="middle" fontFamily="IBM Plex Mono" fontSize="7.5" fill={C.stormLo}>biallelic loss</text>
        <line x1="118" y1="125" x2="202" y2="153" stroke={C.stormLo} strokeWidth="1" />
      </g>

      {/* flow 2: through gate → AAA */}
      <line id="flow-2" opacity={o('flow-2')} x1="160" y1="160" x2="160" y2="188" stroke={C.violetLo} strokeWidth="1.5" markerEnd="url(#ma-v)" style={{ transition: 'opacity 0.5s' }} />

      {/* α-aminoadipate (normal exit) */}
      <g id="aaa" opacity={o('aaa')} style={{ transition: 'opacity 0.5s' }}>
        <circle cx="160" cy="215" r="28" fill={C.bg} stroke={C.violetLo} strokeWidth="1.2" />
        <text x="160" y="213" textAnchor="middle" fontFamily="Syne" fontSize="10" fill={C.ink}>α-AAA</text>
        <text x="160" y="225" textAnchor="middle" fontFamily="IBM Plex Mono" fontSize="7" fill={C.dim}>cleared</text>
      </g>

      {/* P6C — accumulation (replaces normal exit when antq fails) */}
      <g id="p6c" opacity={o('p6c')} style={{ transition: 'opacity 0.5s' }} filter="url(#glow-v)">
        <circle cx="160" cy="215" r="34" fill="rgba(225,29,107,0.07)" stroke={C.storm} strokeWidth="1.4" />
        <text x="160" y="212" textAnchor="middle" fontFamily="Syne" fontSize="11" fill={C.storm}>P6C</text>
        <text x="160" y="225" textAnchor="middle" fontFamily="IBM Plex Mono" fontSize="7" fill={C.stormLo}>↑ accumulating</text>
        <circle cx="160" cy="215" r="48" fill="none" stroke="rgba(225,29,107,0.20)" strokeWidth="1" strokeDasharray="3 4" />
      </g>

      {/* PLP reservoir — right side */}
      <g id="plp-pool" opacity={o('plp-pool')} style={{ transition: 'opacity 0.5s' }}>
        <rect x="360" y="80" width="110" height="140" rx="2" fill="url(#pool-grad)" stroke={C.amberLo} strokeWidth="1.2" />
        <rect x="360" y="80" width="110" height="140" rx="2" fill="url(#dots)" />
        <text x="415" y="68" textAnchor="middle" fontFamily="IBM Plex Mono" fontSize="8" letterSpacing="0.15em" fill={C.amber}>PLP POOL</text>
        <text x="415" y="240" textAnchor="middle" fontFamily="IBM Plex Mono" fontSize="7" fill={C.dim}>active B6 · cofactor</text>
        {/* tick marks like a fuel gauge */}
        <line x1="358" y1="100" x2="354" y2="100" stroke={C.amberLo} strokeWidth="1" />
        <line x1="358" y1="140" x2="354" y2="140" stroke={C.amberLo} strokeWidth="1" />
        <line x1="358" y1="180" x2="354" y2="180" stroke={C.amberLo} strokeWidth="1" />
      </g>

      {/* Trap — siphon from PLP pool to P6C */}
      <g id="trap" opacity={o('trap')} style={{ transition: 'opacity 0.5s' }}>
        <path
          d="M 360 150 C 320 150, 260 200, 200 215"
          stroke={C.storm}
          strokeWidth="1.6"
          strokeDasharray="4 3"
          fill="none"
          markerEnd="url(#ma-s)"
        />
        <text x="290" y="178" textAnchor="middle" fontFamily="IBM Plex Mono" fontSize="7.5" fill={C.storm} letterSpacing="0.12em">PLP SEQUESTERED</text>
        {/* draining drops */}
        <circle cx="340" cy="170" r="2" fill={C.amber} opacity="0.7">
          <animate attributeName="cy" values="160;200;160" dur="2s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.8;0;0.8" dur="2s" repeatCount="indefinite" />
        </circle>
        <circle cx="305" cy="190" r="2" fill={C.amber} opacity="0.6">
          <animate attributeName="cy" values="180;215;180" dur="2.4s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.7;0;0.7" dur="2.4s" repeatCount="indefinite" />
        </circle>
      </g>

      {/* Rescue — B6 raining into pool */}
      <g id="rescue" opacity={o('rescue')} style={{ transition: 'opacity 0.5s' }}>
        <text x="415" y="50" textAnchor="middle" fontFamily="IBM Plex Mono" fontSize="8" fill={C.amber} letterSpacing="0.15em">+ B6 FLOOD</text>
        {[0, 1, 2, 3, 4].map((i) => (
          <circle key={i} cx={375 + i * 20} cy="60" r="2" fill={C.amber}>
            <animate attributeName="cy" values="55;82;55" dur={`${1.2 + i * 0.15}s`} repeatCount="indefinite" begin={`${i * 0.18}s`} />
            <animate attributeName="opacity" values="1;0.2;1" dur={`${1.2 + i * 0.15}s`} repeatCount="indefinite" begin={`${i * 0.18}s`} />
          </circle>
        ))}
      </g>

      {/* GAD enzyme + GABA — bottom right */}
      <g id="gad" opacity={o('gad')} style={{ transition: 'opacity 0.5s' }}>
        <line id="flow-3-line" x1="415" y1="220" x2="415" y2="258" stroke={C.violetLo} strokeWidth="1.2" markerEnd="url(#ma-v)" />
        <rect x="378" y="260" width="74" height="28" rx="2" fill="rgba(13,148,136,0.09)" stroke="rgba(13,148,136,0.5)" strokeWidth="1" />
        <text x="415" y="278" textAnchor="middle" fontFamily="IBM Plex Mono" fontSize="8" fill={C.calm}>GAD · GABA</text>
      </g>

      <line id="flow-3" opacity={o('flow-3')} x1="415" y1="290" x2="415" y2="310" stroke="rgba(13,148,136,0.5)" strokeWidth="1.2" style={{ transition: 'opacity 0.5s' }} />

      <g id="gaba" opacity={o('gaba')} style={{ transition: 'opacity 0.5s' }}>
        <text x="415" y="325" textAnchor="middle" fontFamily="Syne" fontSize="11" fill={C.calm}>inhibition</text>
        <text x="415" y="338" textAnchor="middle" fontFamily="IBM Plex Mono" fontSize="7" fill={C.dim}>cortical balance</text>
      </g>

      {/* Storm — replaces gaba/gad when seizing */}
      <g id="storm" opacity={o('storm')} style={{ transition: 'opacity 0.5s' }} filter="url(#glow-s)">
        <circle cx="415" cy="290" r="42" fill="rgba(225,29,107,0.07)" stroke={C.storm} strokeWidth="1.5" />
        {[...Array(8)].map((_, i) => {
          const a = (i / 8) * Math.PI * 2;
          const x1 = 415 + Math.cos(a) * 30;
          const y1 = 290 + Math.sin(a) * 30;
          const x2 = 415 + Math.cos(a) * 50;
          const y2 = 290 + Math.sin(a) * 50;
          return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke={C.storm} strokeWidth="1.4" opacity="0.7" />;
        })}
        <text x="415" y="288" textAnchor="middle" fontFamily="Syne" fontSize="10" fill={C.storm}>seizure</text>
        <text x="415" y="300" textAnchor="middle" fontFamily="IBM Plex Mono" fontSize="7" fill={C.stormLo}>refractory</text>
      </g>
    </svg>
  );
}

// ─── CATABOLIC CASCADE ────────────────────────────────────────────────────────
// Horizontal lysine catabolism: saccharopine pathway with the antiquitin block
// and the AASA ⇌ P6C equilibrium that fuels the PLP trap.
function CascadeSVG() {
  // Node positions along the horizontal flow
  const nodes = [
    { id: 'lys',   x: 60,   label: 'Lysine',          sub: 'C₆H₁₄N₂O₂',         tone: 'ink' },
    { id: 'sac',   x: 220,  label: 'Saccharopine',    sub: 'intermediate',      tone: 'ink' },
    { id: 'aasa',  x: 400,  label: 'α-AASA',          sub: 'open-chain aldehyde', tone: 'storm' },
    { id: 'p6c',   x: 540,  label: 'P6C',             sub: 'cyclic imine',      tone: 'storm' },
    { id: 'aaa',   x: 720,  label: 'α-aminoadipate',  sub: 'cleared form',      tone: 'dim' },
    { id: 'aka',   x: 890,  label: 'α-ketoadipate',   sub: '',                  tone: 'dim' },
    { id: 'glcoa', x: 1050, label: 'glutaryl-CoA',    sub: '… → acetyl-CoA',    tone: 'dim' },
  ];
  const toneColor = (t) => (t === 'ink' ? C.ink : t === 'storm' ? C.storm : t === 'dim' ? C.dim : C.violet);

  // Enzyme labels on the arrows between consecutive nodes (except the AASA⇌P6C equilibrium drawn separately)
  const enzymes = [
    { from: 0, to: 1, name: 'LKR',        sub: 'AASS · domain 1' },
    { from: 1, to: 2, name: 'SDH',        sub: 'AASS · domain 2' },
    // 2 → 3 is the equilibrium, drawn separately
    { from: 3, to: 4, name: 'ANTIQUITIN', sub: 'ALDH7A1 ✕ blocked', bad: true },
    { from: 4, to: 5, name: 'AADAT',      sub: '' },
    { from: 5, to: 6, name: 'DHTKD1',     sub: '' },
  ];

  const W = 1140;
  const H = 240;
  const yMid = 130;

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      xmlns="http://www.w3.org/2000/svg"
      style={{ width: '100%', minWidth: 980, height: 'auto', overflow: 'visible' }}
    >
      <defs>
        <marker id="cm-v" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
          <path d="M0,0 L0,6 L7,3z" fill={C.violetLo} />
        </marker>
        <marker id="cm-d" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
          <path d="M0,0 L0,6 L7,3z" fill="rgba(106,100,126,0.7)" />
        </marker>
        <marker id="cm-s" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
          <path d="M0,0 L0,6 L7,3z" fill={C.stormLo} />
        </marker>
        <marker id="cm-a" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
          <path d="M0,0 L0,6 L7,3z" fill={C.amberLo} />
        </marker>
      </defs>

      {/* enzyme arrows between consecutive nodes */}
      {enzymes.map((e, i) => {
        const a = nodes[e.from];
        const b = nodes[e.to];
        const x1 = a.x + 38;
        const x2 = b.x - 38;
        const isBad = e.bad;
        const stroke = isBad ? C.storm : C.violetLo;
        const marker = isBad ? 'url(#cm-s)' : 'url(#cm-v)';
        return (
          <g key={i}>
            <line
              x1={x1} y1={yMid} x2={x2} y2={yMid}
              stroke={stroke}
              strokeWidth={isBad ? 1.6 : 1.4}
              strokeDasharray={isBad ? '5 4' : undefined}
              markerEnd={marker}
            />
            <text
              x={(x1 + x2) / 2}
              y={yMid - 14}
              textAnchor="middle"
              fontFamily="IBM Plex Mono"
              fontSize="8.5"
              letterSpacing="0.12em"
              fill={isBad ? C.storm : C.violet}
            >
              {e.name}
            </text>
            {e.sub && (
              <text
                x={(x1 + x2) / 2}
                y={yMid + 22}
                textAnchor="middle"
                fontFamily="IBM Plex Mono"
                fontSize="7"
                fill={isBad ? C.stormLo : C.dim}
              >
                {e.sub}
              </text>
            )}
            {/* block X on the broken arrow */}
            {isBad && (
              <g>
                <line x1={(x1 + x2) / 2 - 6} y1={yMid - 6} x2={(x1 + x2) / 2 + 6} y2={yMid + 6} stroke={C.storm} strokeWidth="1.6" />
                <line x1={(x1 + x2) / 2 - 6} y1={yMid + 6} x2={(x1 + x2) / 2 + 6} y2={yMid - 6} stroke={C.storm} strokeWidth="1.6" />
              </g>
            )}
          </g>
        );
      })}

      {/* AASA ⇌ P6C equilibrium */}
      <g>
        <path
          d={`M ${nodes[2].x + 36} ${yMid - 6} Q ${(nodes[2].x + nodes[3].x) / 2} ${yMid - 28} ${nodes[3].x - 36} ${yMid - 6}`}
          stroke={C.stormLo}
          strokeWidth="1.3"
          fill="none"
          markerEnd="url(#cm-s)"
        />
        <path
          d={`M ${nodes[3].x - 36} ${yMid + 6} Q ${(nodes[2].x + nodes[3].x) / 2} ${yMid + 28} ${nodes[2].x + 36} ${yMid + 6}`}
          stroke={C.stormLo}
          strokeWidth="1.3"
          fill="none"
          markerEnd="url(#cm-s)"
        />
        <text
          x={(nodes[2].x + nodes[3].x) / 2}
          y={yMid - 36}
          textAnchor="middle"
          fontFamily="IBM Plex Mono"
          fontSize="8"
          letterSpacing="0.12em"
          fill={C.storm}
        >
          spontaneous
        </text>
        <text
          x={(nodes[2].x + nodes[3].x) / 2}
          y={yMid + 48}
          textAnchor="middle"
          fontFamily="IBM Plex Mono"
          fontSize="7"
          fill={C.stormLo}
        >
          equilibrium · pH-driven
        </text>
      </g>

      {/* PLP trap branch — drops from P6C downward to a sequestered PLP node */}
      <g>
        <path
          d={`M ${nodes[3].x} ${yMid + 36} Q ${nodes[3].x + 30} ${yMid + 80} ${nodes[3].x + 80} ${yMid + 90}`}
          stroke={C.amberLo}
          strokeWidth="1.4"
          strokeDasharray="4 3"
          fill="none"
          markerEnd="url(#cm-a)"
        />
        <text
          x={nodes[3].x + 6}
          y={yMid + 70}
          fontFamily="IBM Plex Mono"
          fontSize="7.5"
          fill={C.amber}
          letterSpacing="0.1em"
        >
          + PLP
        </text>
        <g transform={`translate(${nodes[3].x + 80}, ${yMid + 92})`}>
          <rect x="-44" y="-12" width="88" height="26" rx="2" fill="rgba(217,119,6,0.10)" stroke={C.amberLo} strokeWidth="1" strokeDasharray="3 3" />
          <text x="0" y="-1" textAnchor="middle" fontFamily="IBM Plex Mono" fontSize="8" fill={C.amber}>P6C–PLP adduct</text>
          <text x="0" y="10" textAnchor="middle" fontFamily="IBM Plex Mono" fontSize="7" fill={C.dim}>inactive</text>
        </g>
      </g>

      {/* upstream — pipecolate minor route into P6C */}
      <g>
        <path
          d={`M ${nodes[0].x + 20} ${yMid + 60} Q ${nodes[1].x} ${yMid + 110} ${nodes[3].x - 36} ${yMid + 18}`}
          stroke="rgba(106,100,126,0.5)"
          strokeWidth="1"
          strokeDasharray="2 4"
          fill="none"
          markerEnd="url(#cm-d)"
        />
        <text
          x={(nodes[0].x + nodes[3].x) / 2 - 30}
          y={yMid + 118}
          textAnchor="middle"
          fontFamily="IBM Plex Mono"
          fontSize="7"
          fill={C.dim}
          letterSpacing="0.1em"
        >
          minor · pipecolate route (brain)
        </text>
      </g>

      {/* nodes */}
      {nodes.map((n) => {
        const isAcc = n.tone === 'storm';
        const fill = isAcc ? 'rgba(225,29,107,0.07)' : C.bg;
        const stroke = isAcc ? C.storm : n.tone === 'ink' ? C.violetLo : 'rgba(106,100,126,0.5)';
        return (
          <g key={n.id}>
            {isAcc && (
              <circle cx={n.x} cy={yMid} r="44" fill="none" stroke="rgba(225,29,107,0.22)" strokeWidth="1" strokeDasharray="3 4" />
            )}
            <circle cx={n.x} cy={yMid} r="34" fill={fill} stroke={stroke} strokeWidth="1.3" />
            <text x={n.x} y={yMid - 1} textAnchor="middle" fontFamily="Syne" fontSize="10" fill={toneColor(n.tone === 'dim' ? 'dim' : 'ink')}>{n.label}</text>
            {n.sub && (
              <text x={n.x} y={yMid + 12} textAnchor="middle" fontFamily="IBM Plex Mono" fontSize="6.5" fill={C.dim}>{n.sub}</text>
            )}
            {isAcc && (
              <text x={n.x} y={yMid - 50} textAnchor="middle" fontFamily="IBM Plex Mono" fontSize="7" fill={C.storm} letterSpacing="0.12em">↑ ACCUMULATES</text>
            )}
          </g>
        );
      })}

      {/* compartment label */}
      <text x="20" y="24" fontFamily="IBM Plex Mono" fontSize="8" letterSpacing="0.2em" fill={C.dim} textTransform="uppercase">SACCHAROPINE PATHWAY · MITOCHONDRION → CYTOSOL</text>
    </svg>
  );
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
export default function PDEPathway() {
  const [stepIdx, setStepIdx] = useState(0);
  const [activeMol, setActiveMol] = useState('plp');
  const [dose, setDose] = useState('off'); // off | std | high

  // Refs
  const heroTagRef = useRef(null);
  const heroH1Ref = useRef(null);
  const heroPRef = useRef(null);
  const heroMetaRef = useRef(null);
  const numRef = useRef(null);
  const titleRef = useRef(null);
  const bodyRef = useRef(null);
  const badgeRef = useRef(null);
  const pathwayRef = useRef(null);
  const drawerRef = useRef(null);
  const canvasRefs = useRef({});

  const currentStep = STEPS[stepIdx];

  // Build vis map
  const vis = {};
  currentStep.show.forEach((id) => { vis[id] = 1; });
  currentStep.hide.forEach((id) => { vis[id] = 0; });

  // Fonts
  useEffect(() => {
    if (!document.querySelector(`link[href="${FONT_URL}"]`)) {
      const link = Object.assign(document.createElement('link'), { rel: 'stylesheet', href: FONT_URL });
      document.head.appendChild(link);
    }
  }, []);

  // SmilesDrawer init
  useEffect(() => {
    drawerRef.current = new SmilesDrawer({
      width: 220,
      height: 130,
      bondThickness: 0.9,
      themes: {
        violet: {
          C: '#2a2440',
          O: '#d97706',
          N: '#7c3aed',
          H: '#8b86a0',
          S: '#ca8a04',
          P: '#e11d6b',
          BACKGROUND: 'transparent',
        },
      },
    });
  }, []);

  // Draw all molecule canvases once drawer is ready
  useEffect(() => {
    if (!drawerRef.current) return;
    Object.keys(MOLS).forEach((key) => {
      const id = `mol-canvas-${key}`;
      if (document.getElementById(id)) {
        try {
          drawerRef.current.draw(MOLS[key].smiles, `#${id}`, 'violet', false);
        } catch (e) {
          // Some SMILES strings (P6C cyclic imine) may need a retry pass
        }
      }
    });
  }, []);

  // Hero entrance
  useEffect(() => {
    const els = [heroTagRef.current, heroH1Ref.current, heroPRef.current, heroMetaRef.current];
    gsap.set(els, { y: 24, opacity: 0 });
    const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });
    tl.to(heroTagRef.current, { opacity: 1, y: 0, duration: 0.7, delay: 0.2 })
      .to(heroH1Ref.current, { opacity: 1, y: 0, duration: 0.9 }, '-=0.3')
      .to(heroPRef.current, { opacity: 1, y: 0, duration: 0.7 }, '-=0.5')
      .to(heroMetaRef.current, { opacity: 1, y: 0, duration: 0.6 }, '-=0.3');
    return () => tl.kill();
  }, []);

  // Panel text reveal on step change
  const isFirst = useRef(true);
  useEffect(() => {
    if (isFirst.current) { isFirst.current = false; return; }
    const targets = [numRef.current, titleRef.current, bodyRef.current, badgeRef.current];
    const tl = gsap.timeline();
    tl.to(targets, { opacity: 0, y: -10, duration: 0.18, stagger: 0.04 })
      .to(targets, { opacity: 1, y: 0, duration: 0.32, stagger: 0.06 });
    return () => tl.kill();
  }, [stepIdx]);

  // Pinned scroll trigger
  useEffect(() => {
    if (!pathwayRef.current) return;
    const total = STEPS.length;
    const trigger = ScrollTrigger.create({
      trigger: pathwayRef.current,
      start: 'top top',
      end: `+=${window.innerHeight * (total - 1)}`,
      pin: true,
      scrub: 0.6,
      snap: { snapTo: 1 / (total - 1), duration: 0.3, ease: 'power2.out' },
      onUpdate: (self) => {
        const idx = Math.round(self.progress * (total - 1));
        setStepIdx((prev) => (prev !== idx ? idx : prev));
      },
    });
    return () => trigger.kill();
  }, []);

  // Dose → derived PLP pool level for the rescue section
  const poolLevel = dose === 'off' ? 0.12 : dose === 'std' ? 0.45 : 0.92;
  const rescueEEG = dose === 'off' ? 'seize' : dose === 'std' ? 'flutter' : 'calm';

  return (
    <div style={S.root}>
      {/* HERO */}
      <section style={S.hero}>
        <div style={S.heroEEG}>
          <EEG mode="seize" width={1600} height={400} color={C.storm} />
        </div>
        <div ref={heroTagRef} style={S.heroTag}>OMIM 266100 · ALDH7A1</div>
        <h1 ref={heroH1Ref} style={S.heroH1}>
          The cofactor<br />
          gets <em style={{ color: C.storm, fontStyle: 'italic' }}>stolen</em>.<br />
          The brain<br />
          stops listening.
        </h1>
        <p ref={heroPRef} style={S.heroP}>
          Pyridoxine-dependent epilepsy is not a vitamin deficiency. It is a vitamin <em>trap</em> — a metabolite that binds active B6 and starves the inhibitory circuitry of the neonatal brain.
        </p>
        <div ref={heroMetaRef} style={S.heroMeta}>
          <span>Prevalence · 1:400,000</span>
          <span>Onset · neonatal</span>
          <span>Therapy · B6 for life</span>
        </div>
      </section>

      {/* PATHWAY (pinned, chapter rail + diagram + copy) */}
      <section ref={pathwayRef} style={S.pathwaySection}>
        <div style={S.pathwaySticky}>
          {/* chapter rail */}
          <div style={S.chapterRail}>
            {STEPS.map((s, i) => (
              <div key={i} style={S.chapterItem(i === stepIdx)}>
                {s.chapter}
              </div>
            ))}
          </div>

          {/* diagram column with live EEG strip */}
          <div style={S.diagramWrap}>
            <div style={S.eegStrip}>
              <div style={S.eegLabel}>EEG · cortex</div>
              <div style={S.eegReading(currentStep.eeg)}>
                {currentStep.eeg === 'seize' ? 'burst-suppression' : currentStep.eeg === 'flutter' ? 'irritable' : 'background normal'}
              </div>
              <EEG mode={currentStep.eeg} />
            </div>
            <PathwaySVG vis={vis} />
          </div>

          {/* copy */}
          <div style={S.stepPanel}>
            <div ref={numRef} style={S.stepNum}>{currentStep.num}</div>
            <div ref={titleRef} style={S.stepTitle}>{currentStep.title}</div>
            <div ref={bodyRef} style={S.stepBody}>{currentStep.body}</div>
            <span ref={badgeRef} style={badgeStyle(currentStep.tone)}>{currentStep.badge}</span>
          </div>
        </div>
      </section>

      {/* CATABOLIC CASCADE — full lysine degradation map */}
      <section style={S.cascadeSection}>
        <div style={S.cascadeHead}>
          <div>
            <div style={S.cascadeLabel}>Lysine catabolism · the long view</div>
            <div style={S.cascadeHeading}>One blocked step. Two metabolites in revolt.</div>
          </div>
          <p style={S.cascadeBlurb}>
            Lysine flows through the saccharopine pathway in the mitochondrial matrix. Antiquitin sits exactly at the AASA → α-aminoadipate step. Knock it out and α-AASA piles up — and because AASA is in spontaneous equilibrium with its cyclic form P6C, the pool that traps PLP keeps refilling itself.
          </p>
        </div>

        <div style={S.cascadeSVGWrap}>
          <CascadeSVG />
        </div>

        <div style={S.cascadeLegend}>
          <span><span style={S.legendSwatch(C.violet)} />active step</span>
          <span><span style={S.legendSwatch(C.storm)} />blocked / accumulating</span>
          <span><span style={S.legendSwatch(C.amber)} />cofactor sequestration</span>
          <span><span style={S.legendSwatch('rgba(106,100,126,0.6)')} />minor route</span>
        </div>
      </section>

      {/* RESCUE INTERACTIVE */}
      <section style={S.rescueSection}>
        <div style={S.rescueGrid}>
          <div>
            <div style={S.rescueLabel}>Interactive · dose-response</div>
            <div style={S.rescueHeading}>Refill the pool.</div>
            <p style={S.rescueCopy}>
              The trap doesn't disappear — therapy outruns it. Adjust the pyridoxine dose to see how the PLP pool refills and cortical activity calms.
            </p>
            <div style={S.doseToggle}>
              <button style={S.doseBtn(dose === 'off')} onClick={() => setDose('off')}>Untreated</button>
              <button style={S.doseBtn(dose === 'std')} onClick={() => setDose('std')}>Standard</button>
              <button style={S.doseBtn(dose === 'high')} onClick={() => setDose('high')}>Pharmacologic</button>
            </div>
          </div>

          {/* Visual: PLP gauge + live EEG */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.2rem' }}>
            <div style={{ position: 'relative', height: 200, border: '1px solid rgba(27,21,48,0.12)', background: 'rgba(27,21,48,0.035)' }}>
              <div
                style={{
                  position: 'absolute',
                  bottom: 0, left: 0, right: 0,
                  height: `${poolLevel * 100}%`,
                  background: `linear-gradient(180deg, rgba(217,119,6,0), rgba(217,119,6,0.30))`,
                  borderTop: `1px solid ${C.amber}`,
                  transition: 'height 0.7s cubic-bezier(.5,0,.2,1)',
                }}
              />
              <div style={{ position: 'absolute', top: 12, left: 14, fontFamily: "'IBM Plex Mono', monospace", fontSize: '0.6rem', letterSpacing: '0.2em', color: C.amber, textTransform: 'uppercase' }}>
                PLP pool · {Math.round(poolLevel * 100)}%
              </div>
              <div style={{ position: 'absolute', bottom: 12, right: 14, fontFamily: "'Instrument Serif', serif", fontSize: '2rem', color: C.amber }}>
                {dose === 'off' ? '0' : dose === 'std' ? '5 mg/kg' : '30 mg/kg'}
              </div>
            </div>
            <div style={{ height: 90, border: '1px solid rgba(27,21,48,0.12)', position: 'relative', background: 'rgba(27,21,48,0.035)' }}>
              <div style={{ position: 'absolute', top: 10, left: 14, fontFamily: "'IBM Plex Mono', monospace", fontSize: '0.55rem', letterSpacing: '0.2em', color: C.dim, textTransform: 'uppercase' }}>EEG</div>
              <EEG mode={rescueEEG} height={90} />
            </div>
          </div>
        </div>
      </section>

      {/* MOLECULE VIEWER — card grid (different from PKU's tab+canvas) */}
      <section style={S.viewerSection}>
        <div style={S.viewerLabel}>Cast of molecules</div>
        <div style={S.viewerHeading}>Six structures, one heist.</div>

        <div style={S.molGrid}>
          {Object.keys(MOLS).map((key) => {
            const m = MOLS[key];
            return (
              <div
                key={key}
                style={S.molCard(activeMol === key)}
                onClick={() => setActiveMol(key)}
              >
                <div style={S.molRole(m.role)}>{m.role}</div>
                <div style={S.molName}>{m.name}</div>
                <div style={S.molCanvasBox}>
                  <canvas
                    id={`mol-canvas-${key}`}
                    ref={(el) => { canvasRefs.current[key] = el; }}
                    width={220}
                    height={130}
                    style={{ display: 'block', maxWidth: '100%' }}
                  />
                </div>
                <div style={S.molDesc}>{m.desc}</div>
              </div>
            );
          })}
        </div>
      </section>

      {/* HOW YOU CAN HELP */}
      <section style={{ padding: '8rem 6vw 4rem', borderTop: `1px solid ${C.violetLo}`, background: C.bgAlt }}>
        <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '0.6rem', letterSpacing: '0.25em', color: C.violet, textTransform: 'uppercase', marginBottom: '0.8rem' }}>Get Involved</div>
        <div style={{ fontFamily: "'Instrument Serif', serif", fontSize: 'clamp(2rem, 4vw, 3.4rem)', marginBottom: '1.4rem' }}>How You Can Help</div>
        <p style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '0.78rem', color: '#6c6685', lineHeight: 1.85, maxWidth: '640px', marginBottom: '3rem' }}>
          PDE is vanishingly rare, which means research depends almost entirely on families finding each other and funding the work. Natural-history data and gene-therapy trials only happen when the community organizes.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.6rem', maxWidth: '860px', marginBottom: '3.5rem' }}>
          {[
            { t: 'Give', d: 'Fund the natural-history studies and gene-therapy research that PDE families depend on.' },
            { t: 'Connect families', d: 'Help newly diagnosed families reach the PDE community and the registries that power trials.' },
            { t: 'Raise awareness', d: 'Earlier recognition of pyridoxine-dependent seizures means earlier treatment and better outcomes.' },
          ].map((w) => (
            <div key={w.t} style={{ borderTop: `2px solid ${C.violet}`, paddingTop: '1rem' }}>
              <div style={{ fontFamily: "'Syne', sans-serif", fontSize: '1rem', fontWeight: 600, color: C.ink, marginBottom: '0.5rem' }}>{w.t}</div>
              <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '0.72rem', color: '#6c6685', lineHeight: 1.8 }}>{w.d}</div>
            </div>
          ))}
        </div>
        <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '0.6rem', letterSpacing: '0.25em', color: C.dim, textTransform: 'uppercase', marginBottom: '1.1rem' }}>Foundations Worth Supporting</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', maxWidth: '860px' }}>
          {[
            { name: 'National Organization for Rare Disorders (NORD)', desc: 'Patient assistance programs and advocacy across rare conditions.', url: 'https://rarediseases.org' },
            { name: 'Epilepsy Foundation', desc: 'Helpline, family support, and advocacy for epilepsy care.', url: 'https://www.epilepsy.com' },
            { name: 'CURE Epilepsy', desc: 'Funds research toward a cure for the epilepsies.', url: 'https://www.cureepilepsy.org' },
            { name: 'Child Neurology Foundation', desc: 'Supports families navigating childhood neurologic conditions.', url: 'https://www.childneurologyfoundation.org' },
          ].map((f) => (
            <a key={f.name} href={f.url} target="_blank" rel="noopener noreferrer"
               style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1.5rem', padding: '1.3rem 1.5rem', background: 'rgba(124,58,237,0.05)', border: '1px solid rgba(27,21,48,0.10)', borderRadius: '10px', textDecoration: 'none', transition: 'border-color 0.18s ease, background 0.18s ease' }}
               onMouseEnter={(e) => { e.currentTarget.style.borderColor = C.violetLo; e.currentTarget.style.background = 'rgba(124,58,237,0.10)'; }}
               onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(27,21,48,0.10)'; e.currentTarget.style.background = 'rgba(124,58,237,0.05)'; }}>
              <div>
                <div style={{ fontFamily: "'Syne', sans-serif", fontSize: '0.9rem', fontWeight: 600, color: C.ink, marginBottom: '0.3rem' }}>{f.name}</div>
                <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '0.7rem', color: '#6c6685', lineHeight: 1.65 }}>{f.desc}</div>
              </div>
              <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '1rem', color: C.violet, flexShrink: 0 }}>&rarr;</span>
            </a>
          ))}
        </div>
      </section>

      {/* SOURCES */}
      <section style={S.sourcesSection}>
        <div style={S.sourcesLabel}>References</div>
        <div style={S.sourcesHeading}>Sources</div>
        {SOURCES.map((s, i) => (
          <div key={i} style={S.sourceRow}>
            <span style={S.sourceIdx}>{String(i + 1).padStart(2, '0')}</span>
            <a
              href={s.href}
              target="_blank"
              rel="noopener noreferrer"
              style={S.sourceCite}
            >
              {s.cite}
            </a>
          </div>
        ))}
      </section>

      <footer style={S.footer}>
        Educational illustration · not for clinical use · last reviewed 2026
      </footer>

      <ContactFooter />
    </div>
  );
}
